import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import classes from "./Introduction.module.css";
import { env } from "../../../constants/env";
import axios from "axios";
import CommonButton from "../../ui/CommonButton/CommonButton";
import InputField from "../../ui/InputField/InputField";

const Introduction = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isfetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userRole = user?.role;
  const [editable, setEditable] = useState(userRole === "ASSESSOR");

  // ✅ Extra form fields (will be saved in metadata)
  const [formFields, setFormFields] = useState({
    assessor: "",
    observers: "",
    areaOfOperation: "",
    labRepresentative: "",
  });

  const handleFormFieldChange = (field, value) => {
    setFormFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAnswerChange = (questionId, score, comment, points) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { score, comment, points },
    }));
  };

  useEffect(() => {
    if (!labId) return;
    fetchAccreditation();
  }, [labId, token, user._id]);

  const fetchAccreditation = async () => {
    setIsFetching(true);
    axios
      .get(`${env.kataApiUrl}/api/accreditation`, {
        params: {
          labId,
          section: "Introduction",
          name: "SADCAS F134-A",
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.success && res.data.accreditation) {
          const accreditation = res.data.accreditation;

          if (accreditation.auditorId !== user._id || userRole !== "ASSESSOR") {
            setEditable(false);
          }

          if (accreditation.answers) {
            const savedAnswers = accreditation.answers || [];
            const mapped = savedAnswers.reduce((acc, a) => {
              acc[a.questionId] = {
                score: a.score || "",
                comment: a.comment || "",
                points: a.points || 0,
              };
              return acc;
            }, {});
            setAnswers(mapped);
            setEditable(false); // ✅ answers exist → read-only
          } else {
            setEditable(true);
          }

          // ✅ Prefill extra form fields from metadata
          if (accreditation.metadata) {
            setFormFields({
              assessor: accreditation.metadata.assessor || "",
              observers: accreditation.metadata.observers || "",
              areaOfOperation: accreditation.metadata.areaOfOperation || "",
              labRepresentative: accreditation.metadata.labRepresentative || "",
            });
          }
        }
      })
      .catch((err) => {
        console.error("Failed to fetch accreditation:", err);
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  const questionSets = [
    {
      questions: [
        {
          questionId: "Q1",
          content:
            "The schedule of accredited tests shall include only the tests that are performed by the Laboratory.",
          number: "1",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q2",
          content: "The relevant staff have access to SADCAS documentation",
          number: "2",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q3",
          content:
            "A current list of approved signatories for each discipline within the laboratory is available.",
          number: "3",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      render: (onAnswerChange, answers) => (
        <QuestionSection
          number="1"
          title="Introduction"
          accreditation
          showPointsField={false}
          questions={questionSets[0].questions}
          onAnswerChange={editable ? onAnswerChange : () => {}}
          answers={answers}
          disabled={!editable}
        />
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  const handleDone = async () => {
    setIsSubmitting(true);
    try {
      const answersArray = Object.entries(answers).map(([questionId, data]) => {
        let questionContent;
        questionSets.forEach((set) => {
          set.questions?.forEach((q) => {
            if (q.questionId === questionId) questionContent = q.content;
          });
        });

        return {
          questionId,
          content: questionContent,
          score: data.score || undefined,
          comment: data.comment,
          points: data.points,
        };
      });

      const response = await fetch(`${env.kataApiUrl}/api/accreditation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: "SADCAS F134-A",
          labId,
          section: "Introduction",
          auditorId: user._id,
          answers: answersArray,
          metadata: formFields, // ✅ wrapped in metadata
        }),
      });

      if (!response.ok) throw new Error("Submission failed");

      fetchAccreditation();
      toast.success("Updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to submit assessment");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Introduction</h2>
        </div>
        {userRole === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditable(true)}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS A - Introduction has been submitted.
        </div>
      ) : (
        <>
          {/* ✅ Extra form fields */}
          <div className="row mb-5">
            <div className="col-6 mb-3">
              <InputField
                labelText="Assessor/s"
                placeholder="Enter username"
                value={formFields.assessor}
                onChange={(e) =>
                  handleFormFieldChange("assessor", e.target.value)
                }
                disabled={!editable}
              />
            </div>
            <div className="col-6 mb-3">
              <InputField
                labelText="Observers"
                placeholder="Enter Observers"
                value={formFields.observers}
                onChange={(e) =>
                  handleFormFieldChange("observers", e.target.value)
                }
                disabled={!editable}
              />
            </div>
            <div className="col-6 mb-3">
              <InputField
                labelText="Area/Field of operation"
                placeholder="Enter area"
                value={formFields.areaOfOperation}
                onChange={(e) =>
                  handleFormFieldChange("areaOfOperation", e.target.value)
                }
                disabled={!editable}
              />
            </div>
            <div className="col-6 mb-3">
              <InputField
                labelText="Laboratory representative"
                placeholder="Enter representative"
                value={formFields.labRepresentative}
                onChange={(e) =>
                  handleFormFieldChange("labRepresentative", e.target.value)
                }
                disabled={!editable}
              />
            </div>
          </div>

          {questionSets[currentSet].render(handleAnswerChange, answers)}
        </>
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={isSubmitting ? "Submitting..." : "Update"}
          disabled={isSubmitting}
        />
      )}
    </div>
  );
};

export default Introduction;
